/**
 * PatientStoryStrip — "Real patients, real results" strip.
 * Fetches 2–3 PatientStory records matching a procedure/avatar.
 * Shows first name + city only (never last names).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { Quote, MapPin, ArrowRight } from 'lucide-react';

export default function PatientStoryStrip({ procedure, avatar, accentColor = 'amber' }) {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // PatientStory read is admin-only (RLS); consented stories are served
    // publicly (whitelisted fields) via the getPublicPatientStories function.
    base44.functions.invoke('getPublicPatientStories', { procedure, avatar, limit: 6 })
      .then(res => {
        const data = res?.data?.stories || [];
        // If procedure-filtered result is empty, fall back to avatar-only
        if (data.length === 0 && procedure && avatar) {
          return base44.functions.invoke('getPublicPatientStories', { avatar, limit: 6 })
            .then(r => r?.data?.stories || []);
        }
        return data;
      })
      .then(data => {
        // Limit to 3 stories
        setStories((data || []).slice(0, 3));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [procedure, avatar]);

  // Don't render anything if no stories found
  if (!loading && stories.length === 0) return null;

  const accent = {
    amber: { border: 'border-amber-200', bg: 'bg-amber-50', quote: 'text-amber-400', tag: 'bg-amber-100 text-amber-700', link: 'text-amber-700 hover:text-amber-800', header: 'text-amber-700' },
    rose: { border: 'border-rose-200', bg: 'bg-rose-50', quote: 'text-rose-400', tag: 'bg-rose-100 text-rose-700', link: 'text-rose-700 hover:text-rose-800', header: 'text-rose-700' },
    emerald: { border: 'border-emerald-200', bg: 'bg-emerald-50', quote: 'text-emerald-500', tag: 'bg-emerald-100 text-emerald-700', link: 'text-emerald-700 hover:text-emerald-800', header: 'text-emerald-700' },
    blue: { border: 'border-blue-200', bg: 'bg-blue-50', quote: 'text-blue-400', tag: 'bg-blue-100 text-blue-700', link: 'text-blue-700 hover:text-blue-800', header: 'text-blue-700' },
  }[accentColor] || { border: 'border-amber-200', bg: 'bg-amber-50', quote: 'text-amber-400', tag: 'bg-amber-100 text-amber-700', link: 'text-amber-700 hover:text-amber-800', header: 'text-amber-700' };

  return (
    <section className={`py-14 px-6 ${accent.bg} border-t ${accent.border}`} aria-label="Patient stories">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-baseline justify-between mb-8 gap-4 flex-wrap">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-widest ${accent.header} mb-1`}>Real Patients, Real Results</p>
            <h2 className="text-2xl font-light text-slate-800">
              What patients say about {procedure || 'treatment'} at Trinity Dental Arts
            </h2>
          </div>
          <Link to="/our-results" className={`text-sm font-medium ${accent.link} flex items-center gap-1 whitespace-nowrap`}>
            All stories <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-5">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-2xl h-40 animate-pulse border border-slate-100" />
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-5">
            {stories.map(story => (
              <article key={story.id} className="bg-white rounded-2xl p-6 border border-white shadow-sm flex flex-col gap-3">
                {story.afterPhoto && (
                  <figure className="m-0">
                    <img
                      src={story.afterPhoto}
                      alt={`After ${story.procedure || 'dental treatment'}: ${story.firstName}${story.city ? ` from ${story.city}` : ''} — result by Dr. Gjelaj at Trinity Dental Arts, Trinity FL`}
                      className="w-full h-36 object-cover rounded-xl"
                      loading="lazy"
                    />
                    <figcaption className="text-xs text-slate-400 mt-1 px-0.5">
                      After: {story.procedure || 'dental treatment'}{story.city ? ` — ${story.city}` : ''}
                    </figcaption>
                  </figure>
                )}
                <Quote className={`w-5 h-5 ${accent.quote} flex-shrink-0`} />
                <p className="text-slate-700 text-sm leading-relaxed italic flex-1">
                  "{story.quote}"
                </p>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  <span className="font-semibold text-slate-800 text-sm">{story.firstName}</span>
                  {story.city && (
                    <span className="text-slate-400 text-xs flex items-center gap-0.5">
                      <MapPin className="w-3 h-3" />{story.city}
                    </span>
                  )}
                  {story.procedure && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${accent.tag} ml-auto`}>
                      {story.procedure}
                    </span>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}